import { LoadingOverlay } from '@mantine/core';
import { FC, useContext, useMemo } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useJwtClaims } from '../../Service/Context/AuthContext';
import { EventContext } from '../../Service/Context/EventContext';

type EventContextValue = {
  event?: {
    id: number;
    applicationUserId: number | null;
  };
  loadingEvent: boolean;
};

const EventPermissionGuard: FC = () => {
  const { event, loadingEvent } = useContext(EventContext) as unknown as EventContextValue;
  const { userId, role, scopes = [] } = useJwtClaims();

  const isOwner = !role || role === 'OWNER';
  const hasGeneralEventsScope = isOwner || scopes.includes('GENERAL_EVENTS');
  const hasUnassignedEventsScope = isOwner || scopes.includes('UNASSIGNED_EVENTS');

  const isAllowed = useMemo(() => {
    if (!event?.id) {
      return true;
    }

    if (isOwner || hasGeneralEventsScope) {
      return true;
    }

    if (!event.applicationUserId) {
      return hasUnassignedEventsScope;
    }

    if (!userId) {
      return true;
    }

    return event.applicationUserId === userId;
  }, [userId, event?.applicationUserId, event?.id, hasGeneralEventsScope, hasUnassignedEventsScope, isOwner]);

  if (loadingEvent || !event?.id || (event.applicationUserId && !userId && !isOwner && !hasGeneralEventsScope)) {
    return <LoadingOverlay visible loaderProps={{ size: 'xl' }} />;
  }

  if (!isAllowed) {
    return <Navigate to={`/events/${event.id}/no-permission`} replace />;
  }

  return <Outlet />;
};

export default EventPermissionGuard;
